import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';

interface FoodCostGaugeProps {
  value: number;   // current food cost %
  target: number;  // target food cost %
}

const FoodCostGauge: React.FC<FoodCostGaugeProps> = ({ value, target }) => {
  const { theme } = useTheme();

  const isOverTarget = value > target;
  const gaugeColor = isOverTarget ? (theme.danger || '#D99982') : (theme.success || '#4CAF50');
  const bgTrackColor = theme.gray?.[200] || '#E0E0E0';

  // Clamp the sweep between 0 and 100 for the arc
  const clampedValue = Math.min(Math.max(value, 0), 100);

  // SVG semicircle gauge
  const radius = 60;
  const strokeWidth = 12;
  const cx = 75;
  const cy = 70;
  const circumference = Math.PI * radius; // semicircle
  const dashOffset = circumference - (clampedValue / 100) * circumference;

  // Target line position on the arc
  const targetAngle = Math.PI - (target / 100) * Math.PI;
  const targetX1 = cx + (radius - strokeWidth / 2 - 2) * Math.cos(targetAngle);
  const targetY1 = cy - (radius - strokeWidth / 2 - 2) * Math.sin(targetAngle);
  const targetX2 = cx + (radius + strokeWidth / 2 + 2) * Math.cos(targetAngle);
  const targetY2 = cy - (radius + strokeWidth / 2 + 2) * Math.sin(targetAngle);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box sx={{ position: 'relative', width: 150, height: 90 }}>
        <svg width="150" height="90" viewBox="0 0 150 90">
          {/* Background track */}
          <path
            d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
            fill="none"
            stroke={bgTrackColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Value arc */}
          <path
            d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
            fill="none"
            stroke={gaugeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease' }}
          />
          {/* Target marker line */}
          {target > 0 && target < 100 && (
            <line
              x1={targetX1}
              y1={targetY1}
              x2={targetX2}
              y2={targetY2}
              stroke={theme.text}
              strokeWidth={2}
              strokeLinecap="round"
            />
          )}
        </svg>

        {/* Center value */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.5rem',
              lineHeight: 1,
              color: gaugeColor,
            }}
          >
            {value.toFixed(1)}%
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: theme.gray?.[500] || '#666',
          fontWeight: 600,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Target: {target}%
      </Typography>
    </Box>
  );
};

export default FoodCostGauge;
